import { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Button, Label, Spinner, TextInput } from "flowbite-react";
import { ADMIN, SERASA_ADMIN, SYSTEM_VARIABLES } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";

const CASE_14_CONTRACTS_CONFIG_KEY = "CASE_14_CONTRACTS_CONFIG";

// Agenda semanal — índice 0 = domingo ... 6 = sábado (igual ao Date.getDay()).
// A ordem de exibição começa na segunda-feira.
const WEEKDAYS = [
  { idx: 1, label: "Segunda" },
  { idx: 2, label: "Terça" },
  { idx: 3, label: "Quarta" },
  { idx: 4, label: "Quinta" },
  { idx: 5, label: "Sexta" },
  { idx: 6, label: "Sábado" },
  { idx: 0, label: "Domingo" },
];

const defaultSchedule = () => ({
  enabled: false,
  days: Array.from({ length: 7 }, () => ({
    enabled: true,
    start: "",
    end: "",
  })),
});

const normalizeSchedule = (s) => {
  const base = defaultSchedule();
  if (!s || typeof s !== "object") return base;
  return {
    enabled: s.enabled === true,
    days: base.days.map((d, i) => {
      const src = Array.isArray(s.days) ? s.days[i] : null;
      if (!src || typeof src !== "object") return d;
      return {
        enabled: src.enabled !== false,
        start: typeof src.start === "string" ? src.start : "",
        end: typeof src.end === "string" ? src.end : "",
      };
    }),
  };
};

// ─── helpers ──────────────────────────────────────────────────────────────────

const fetchVariable = async (key) => {
  try {
    const { data } = await axiosInstance.get(
      `${SYSTEM_VARIABLES.GET_BY_KEY}/${encodeURIComponent(key)}`,
    );
    return data?.data?.value ?? null;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

const saveVariable = (key, value, description) =>
  axiosInstance.post(SYSTEM_VARIABLES.UPDATE, { key, value, description });

const fetchCompanies = async () => {
  const { data } = await axiosInstance.get(
    `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1`,
  );
  const list = data?.data?.data ?? [];
  return list.map((c) => ({
    id: c.id,
    label: c.typeCustomer === "CPF" ? c.name : c.socialReason || c.name,
  }));
};

const formatDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const barColor = (requestsToday, maxRequestsPerDay) => {
  if (!maxRequestsPerDay || maxRequestsPerDay <= 0) return "#6366f1";
  const pct = requestsToday / maxRequestsPerDay;
  if (pct >= 0.9) return "#ef4444";
  if (pct >= 0.7) return "#f59e0b";
  return "#10b981";
};

// ─── chart builder ────────────────────────────────────────────────────────────

const CHART_COLORS = {
  basicoPF:    "#10b981",
  basicoPJ:    "#3b82f6",
  avancadoPF:  "#8b5cf6",
  avancadoPJ:  "#f59e0b",
};

const buildChartConfig = (stats) => {
  const labels = stats.contracts.map((_, i) => `Contrato ${i + 1}`);

  const hasDetailedData = stats.contracts.some(
    (c) => (c.basicoPF_ok ?? -1) >= 0,
  );

  const series = hasDetailedData
    ? [
        {
          name: "CPF – Básico",
          data: stats.contracts.map((c) => (c.basicoPF_ok || 0) + (c.basicoPF_err || 0)),
        },
        {
          name: "CNPJ – Básico",
          data: stats.contracts.map((c) => (c.basicoPJ_ok || 0) + (c.basicoPJ_err || 0)),
        },
        {
          name: "CPF – Avançado",
          data: stats.contracts.map((c) => (c.avancadoPF_ok || 0) + (c.avancadoPF_err || 0)),
        },
        {
          name: "CNPJ – Avançado",
          data: stats.contracts.map((c) => (c.avancadoPJ_ok || 0) + (c.avancadoPJ_err || 0)),
        },
      ]
    : [
        { name: "CPF",  data: stats.contracts.map((c) => c.requestsTodayPF || 0) },
        { name: "CNPJ", data: stats.contracts.map((c) => c.requestsTodayPJ || 0) },
      ];

  const colors = hasDetailedData
    ? [CHART_COLORS.basicoPF, CHART_COLORS.basicoPJ, CHART_COLORS.avancadoPF, CHART_COLORS.avancadoPJ]
    : ["#10b981", "#3b82f6"];

  const customTooltip = ({ dataPointIndex }) => {
    const c = stats.contracts[dataPointIndex];
    const total = c.requestsToday;
    const limit = c.maxRequestsPerDay > 0 ? c.maxRequestsPerDay : null;
    const pct   = limit ? Math.round((total / limit) * 100) : null;

    const row = (color, label, ok, err) => {
      const tot = ok + err;
      if (tot === 0) return "";
      return `<div style="display:flex;align-items:center;gap:6px;padding:3px 0">
        <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color};flex-shrink:0"></span>
        <span style="color:#374151;font-size:12px;flex:1">${label}</span>
        <span style="font-size:12px;color:#10b981;font-weight:600">✓${ok}</span>
        ${err > 0 ? `<span style="font-size:12px;color:#ef4444;font-weight:600;margin-left:4px">✗${err}</span>` : ""}
      </div>`;
    };

    const detailRows = hasDetailedData
      ? row(CHART_COLORS.basicoPF,   "CPF – Básico",    c.basicoPF_ok   || 0, c.basicoPF_err   || 0) +
        row(CHART_COLORS.basicoPJ,   "CNPJ – Básico",   c.basicoPJ_ok   || 0, c.basicoPJ_err   || 0) +
        row(CHART_COLORS.avancadoPF, "CPF – Avançado",  c.avancadoPF_ok || 0, c.avancadoPF_err || 0) +
        row(CHART_COLORS.avancadoPJ, "CNPJ – Avançado", c.avancadoPJ_ok || 0, c.avancadoPJ_err || 0)
      : row("#10b981", "CPF",  c.requestsTodayPF || 0, 0) +
        row("#3b82f6", "CNPJ", c.requestsTodayPJ || 0, 0);

    const limitLine = limit
      ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #f1f5f9;font-size:12px;color:#6b7280">
           Limite diário: <strong>${limit}</strong>&nbsp;|&nbsp;Uso: <strong>${pct}%</strong>
         </div>`
      : `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #f1f5f9;font-size:12px;color:#9ca3af">Sem limite diário</div>`;

    return `<div style="padding:10px 14px;min-width:220px;font-family:sans-serif">
               <div style="font-weight:700;font-size:13px;color:#111827;margin-bottom:8px">
                 Contrato ${dataPointIndex + 1}
                 <span style="font-weight:400;color:#6b7280;font-size:11px;margin-left:4px">(${c.clientIdPreview ?? ""})</span>
               </div>
               ${detailRows}
               <div style="margin-top:6px;font-size:12px;font-weight:600;color:#111827">
                 Total: ${total}
               </div>
               ${limitLine}
             </div>`;
  };

  return {
    series,
    options: {
      chart: {
        type: "bar",
        stacked: true,
        height: Math.max(140, stats.contracts.length * 72),
        toolbar: { show: false },
        animations: { enabled: true, speed: 400 },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "55%",
          dataLabels: { position: "center" },
        },
      },
      colors,
      dataLabels: { enabled: false },
      xaxis: {
        categories: labels,
        labels: { style: { fontSize: "13px", fontWeight: 500 } },
      },
      yaxis: {
        labels: { style: { fontSize: "13px", fontWeight: 600 } },
      },
      grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
      legend: {
        show: true,
        position: "bottom",
        fontSize: "12px",
        markers: { size: 8, shape: "square" },
        itemMargin: { horizontal: 10, vertical: 4 },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: customTooltip,
      },
    },
  };
};

// ─── component ────────────────────────────────────────────────────────────────

export default function SerasaSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState({});
  const [openCompanyDropdown, setOpenCompanyDropdown] = useState(null);

  // ── load contracts ──────────────────────────────────────────────────────────

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const raw = await fetchVariable(CASE_14_CONTRACTS_CONFIG_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setContracts(
              parsed.map((c) => ({
                clientId: c.clientId ?? "",
                clientSecret: c.clientSecret ?? "",
                maxRequestsPerDay: c.maxRequestsPerDay ?? 0,
                allowedCompanyIds: Array.isArray(c.allowedCompanyIds)
                  ? c.allowedCompanyIds
                  : [],
                proxyUrl: c.proxyUrl ?? "",
                schedule: normalizeSchedule(c.schedule),
              })),
            );
            return;
          }
        } catch {}
      }
      setContracts([
        {
          clientId: "",
          clientSecret: "",
          maxRequestsPerDay: 0,
          allowedCompanyIds: [],
          proxyUrl: "",
        },
      ]);
    } catch {
      setMessage({ type: "error", text: "Erro ao carregar configurações." });
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = useCallback(async () => {
    try {
      const list = await fetchCompanies();
      setCompanies(list);
    } catch {
      // não bloqueia a página caso a lista de clientes falhe
    }
  }, []);

  // ── load stats ──────────────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await axiosInstance.get(SERASA_ADMIN.GET_STATS);
      setStats(data?.data ?? null);
    } catch {
      // silently ignore — stats are non-critical
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadStats();
    loadCompanies();
  }, [loadStats, loadCompanies]);

  // ── contract mutations ──────────────────────────────────────────────────────

  const updateContract = (index, field, value) => {
    setContracts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    );
  };

  const addContract = () => {
    setContracts((prev) => [
      ...prev,
      {
        clientId: "",
        clientSecret: "",
        maxRequestsPerDay: 0,
        allowedCompanyIds: [],
        proxyUrl: "",
        schedule: defaultSchedule(),
      },
    ]);
  };

  const removeContract = (index) => {
    setContracts((prev) => prev.filter((_, i) => i !== index));
  };

  // ── company bindings ────────────────────────────────────────────────────────

  const assignedCompanyIds = useMemo(
    () => new Set(contracts.flatMap((c) => c.allowedCompanyIds || [])),
    [contracts],
  );

  const addCompanyToContract = (index, companyId) => {
    setContracts((prev) =>
      prev.map((c, i) =>
        i === index
          ? {
              ...c,
              allowedCompanyIds: [...(c.allowedCompanyIds || []), companyId],
            }
          : c,
      ),
    );
    setCompanySearch((prev) => ({ ...prev, [index]: "" }));
    setOpenCompanyDropdown(null);
  };

  const removeCompanyFromContract = (index, companyId) => {
    setContracts((prev) =>
      prev.map((c, i) =>
        i === index
          ? {
              ...c,
              allowedCompanyIds: (c.allowedCompanyIds || []).filter(
                (id) => id !== companyId,
              ),
            }
          : c,
      ),
    );
  };

  // ── schedule (agenda semanal) ────────────────────────────────────────────────

  const mutateSchedule = (index, mutator) => {
    setContracts((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c;
        const schedule = normalizeSchedule(c.schedule);
        return { ...c, schedule: mutator(schedule) };
      }),
    );
  };

  const toggleScheduleEnabled = (index) => {
    mutateSchedule(index, (s) => ({ ...s, enabled: !s.enabled }));
  };

  const toggleScheduleDay = (index, dayIdx) => {
    mutateSchedule(index, (s) => ({
      ...s,
      days: s.days.map((d, i) =>
        i === dayIdx ? { ...d, enabled: !d.enabled } : d,
      ),
    }));
  };

  const updateScheduleDayTime = (index, dayIdx, field, value) => {
    mutateSchedule(index, (s) => ({
      ...s,
      days: s.days.map((d, i) =>
        i === dayIdx ? { ...d, [field]: value } : d,
      ),
    }));
  };

  // ── save ────────────────────────────────────────────────────────────────────

  const save = async () => {
    if (contracts.length === 0) {
      setMessage({ type: "error", text: "Adicione ao menos um contrato." });
      return;
    }

    const hasEmpty = contracts.some(
      (c) => !c.clientId.trim() || !c.clientSecret.trim(),
    );
    if (hasEmpty) {
      setMessage({
        type: "error",
        text: "Preencha Client ID e Client Secret em todos os contratos.",
      });
      return;
    }

    const clientIds = contracts.map((c) => c.clientId.trim());
    if (new Set(clientIds).size !== clientIds.length) {
      setMessage({
        type: "error",
        text: "Existem Client IDs duplicados.",
      });
      return;
    }

    const scheduleWithoutDays = contracts.findIndex(
      (c) => c.schedule?.enabled && !c.schedule.days.some((d) => d.enabled),
    );
    if (scheduleWithoutDays !== -1) {
      setMessage({
        type: "error",
        text: `Contrato ${scheduleWithoutDays + 1}: a agenda está ativa mas nenhum dia da semana foi liberado.`,
      });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const payload = contracts.map((c) => ({
        clientId: c.clientId.trim(),
        clientSecret: c.clientSecret.trim(),
        maxRequestsPerDay: Math.max(0, Number(c.maxRequestsPerDay) || 0),
        allowedCompanyIds: c.allowedCompanyIds || [],
        proxyUrl: (c.proxyUrl || "").trim(),
        schedule: normalizeSchedule(c.schedule),
      }));

      await saveVariable(
        CASE_14_CONTRACTS_CONFIG_KEY,
        JSON.stringify(payload),
        "Contratos Serasa para o case 14.",
      );

      setMessage({ type: "success", text: "Contratos salvos com sucesso." });
      await Promise.all([loadConfig(), loadStats()]);
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || "Erro ao salvar contratos.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="xl" />
      </div>
    );
  }

  const chart =
    stats && stats.contracts.length > 0 ? buildChartConfig(stats) : null;

  return (
    <div className="space-y-6">
      {/* ── contracts card ── */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* header */}
        <div className="border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Contratos Serasa
              </h2>
              <p className="text-xs text-gray-500">
                Distribuição aleatória e alternada — respeita o limite diário de
                cada contrato (0 = ilimitado)
              </p>
            </div>
            <span className="ml-auto rounded-full bg-indigo-100 px-3 py-0.5 text-xs font-semibold text-indigo-700">
              {contracts.length}{" "}
              {contracts.length === 1 ? "contrato" : "contratos"}
            </span>
          </div>
        </div>

        {/* contract list */}
        <div className="divide-y divide-gray-50 px-6 py-4">
          {contracts.map((contract, index) => (
            <div key={index} className="py-4 first:pt-0 last:pb-0">
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                    {index + 1}
                  </span>
                  Contrato {index + 1}
                </span>
                {contracts.length > 1 && (
                  <button
                    onClick={() => removeContract(index)}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    Remover
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <Label
                    htmlFor={`cid-${index}`}
                    value="Client ID"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  />
                  <TextInput
                    id={`cid-${index}`}
                    value={contract.clientId}
                    onChange={(e) =>
                      updateContract(index, "clientId", e.target.value)
                    }
                    placeholder="client_id"
                    sizing="sm"
                  />
                </div>

                <div>
                  <Label
                    htmlFor={`cs-${index}`}
                    value="Client Secret"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  />
                  <TextInput
                    id={`cs-${index}`}
                    type="password"
                    value={contract.clientSecret}
                    onChange={(e) =>
                      updateContract(index, "clientSecret", e.target.value)
                    }
                    placeholder="client_secret"
                    sizing="sm"
                  />
                </div>

                <div>
                  <Label
                    htmlFor={`max-${index}`}
                    value="Máx. req/dia"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  />
                  <div className="relative">
                    <TextInput
                      id={`max-${index}`}
                      type="number"
                      min={0}
                      value={String(contract.maxRequestsPerDay)}
                      onChange={(e) =>
                        updateContract(
                          index,
                          "maxRequestsPerDay",
                          e.target.value,
                        )
                      }
                      placeholder="0"
                      sizing="sm"
                    />
                    {Number(contract.maxRequestsPerDay) === 0 && (
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-400">
                        ∞
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* proxy de IP fixo */}
              <div className="mt-3">
                <Label
                  htmlFor={`proxy-${index}`}
                  value="Proxy URL (opcional)"
                  className="mb-1 block text-xs font-medium text-gray-600"
                />
                <p className="mb-2 text-[11px] text-gray-400">
                  Se preenchido, as requisições deste contrato à Serasa
                  passarão por este proxy (IP fixo). Deixe vazio para usar a
                  conexão direta.
                </p>
                <TextInput
                  id={`proxy-${index}`}
                  value={contract.proxyUrl || ""}
                  onChange={(e) =>
                    updateContract(index, "proxyUrl", e.target.value)
                  }
                  placeholder="http://usuario:senha@host:porta"
                  sizing="sm"
                />
              </div>

              {/* clientes vinculados */}
              <div className="mt-3">
                <Label
                  value="Clientes vinculados (opcional)"
                  className="mb-1 block text-xs font-medium text-gray-600"
                />
                <p className="mb-2 text-[11px] text-gray-400">
                  Se houver clientes vinculados, eles consumirão exclusivamente
                  deste contrato.
                </p>

                <div className="relative mb-3">
                  <TextInput
                    sizing="sm"
                    placeholder="Buscar cliente por nome..."
                    value={companySearch[index] || ""}
                    onChange={(e) =>
                      setCompanySearch((prev) => ({
                        ...prev,
                        [index]: e.target.value,
                      }))
                    }
                    onFocus={() => setOpenCompanyDropdown(index)}
                    onBlur={() =>
                      setTimeout(() => setOpenCompanyDropdown(null), 150)
                    }
                  />
                  {openCompanyDropdown === index &&
                    (companySearch[index] || "").trim() && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                        {companies
                          .filter(
                            (co) =>
                              !assignedCompanyIds.has(co.id) &&
                              co.label
                                ?.toLowerCase()
                                .includes(
                                  (companySearch[index] || "").toLowerCase(),
                                ),
                          )
                          .slice(0, 8)
                          .map((co) => (
                            <button
                              key={co.id}
                              onClick={() => addCompanyToContract(index, co.id)}
                              className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50"
                            >
                              {co.label}
                            </button>
                          ))}
                        {companies.filter(
                          (co) =>
                            !assignedCompanyIds.has(co.id) &&
                            co.label
                              ?.toLowerCase()
                              .includes(
                                (companySearch[index] || "").toLowerCase(),
                              ),
                        ).length === 0 && (
                          <span className="block px-3 py-1.5 text-sm text-gray-400">
                            Nenhum cliente encontrado.
                          </span>
                        )}
                      </div>
                    )}
                </div>
                {contract.allowedCompanyIds?.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {contract.allowedCompanyIds.map((companyId) => {
                      const company = companies.find(
                        (co) => co.id === companyId,
                      );
                      return (
                        <span
                          key={companyId}
                          className="flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
                        >
                          {company?.label || `Cliente #${companyId}`}
                          <button
                            onClick={() =>
                              removeCompanyFromContract(index, companyId)
                            }
                            className="text-indigo-400 hover:text-indigo-700"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* agenda semanal (horários e dias permitidos) */}
              <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold text-gray-700">
                      Agenda de uso (dias e horários)
                    </span>
                    <p className="mt-0.5 text-[11px] text-gray-400">
                      Quando ativa, este contrato só é usado nos dias e horários
                      liberados (horário de Brasília). Fora da janela, a consulta
                      falha.
                    </p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={!!contract.schedule?.enabled}
                      onChange={() => toggleScheduleEnabled(index)}
                    />
                    <div className="h-5 w-9 rounded-full bg-gray-300 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-indigo-600 peer-checked:after:translate-x-4" />
                  </label>
                </div>

                {contract.schedule?.enabled && (
                  <div className="mt-3 space-y-1.5">
                    {WEEKDAYS.map(({ idx, label }) => {
                      const day =
                        contract.schedule.days[idx] || {
                          enabled: false,
                          start: "",
                          end: "",
                        };
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-2 rounded-md bg-white px-2 py-1.5"
                        >
                          <label className="flex w-24 cursor-pointer items-center gap-2 text-xs font-medium text-gray-700">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              checked={!!day.enabled}
                              onChange={() => toggleScheduleDay(index, idx)}
                            />
                            {label}
                          </label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="time"
                              value={day.start || ""}
                              disabled={!day.enabled}
                              onChange={(e) =>
                                updateScheduleDayTime(
                                  index,
                                  idx,
                                  "start",
                                  e.target.value,
                                )
                              }
                              className="rounded-md border-gray-200 px-2 py-1 text-xs disabled:bg-gray-100 disabled:text-gray-300"
                            />
                            <span className="text-xs text-gray-400">até</span>
                            <input
                              type="time"
                              value={day.end || ""}
                              disabled={!day.enabled}
                              onChange={(e) =>
                                updateScheduleDayTime(
                                  index,
                                  idx,
                                  "end",
                                  e.target.value,
                                )
                              }
                              className="rounded-md border-gray-200 px-2 py-1 text-xs disabled:bg-gray-100 disabled:text-gray-300"
                            />
                          </div>
                          {day.enabled && !day.start && !day.end && (
                            <span className="text-[10px] font-medium text-gray-400">
                              dia inteiro
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button
            onClick={addContract}
            className="flex items-center gap-1.5 rounded-lg border border-dashed border-indigo-300 bg-white px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-colors"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Adicionar contrato
          </button>

          <div className="flex items-center gap-3">
            {message.text && (
              <span
                className={`text-sm font-medium ${
                  message.type === "error" ? "text-red-600" : "text-emerald-600"
                }`}
              >
                {message.text}
              </span>
            )}
            <Button onClick={save} isProcessing={saving} size="sm">
              Salvar contratos
            </Button>
          </div>
        </div>
      </div>

      {/* ── stats card ── */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* header */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Consumo de Hoje
              </h2>
              {stats && (
                <p className="text-xs text-gray-500">
                  {formatDate(stats.date)}
                </p>
              )}
            </div>
          </div>

          <button
            onClick={loadStats}
            disabled={loadingStats}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <svg
              className={`h-3.5 w-3.5 ${loadingStats ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Atualizar
          </button>
        </div>

        {/* chart body */}
        <div className="px-6 py-5">
          {loadingStats && !stats ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : !chart ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <svg
                className="mb-3 h-10 w-10 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <p className="text-sm text-gray-400">
                Nenhuma requisição registrada hoje ou nenhum contrato
                configurado.
              </p>
            </div>
          ) : (
            <>
              <ReactApexChart
                key={stats.date}
                options={chart.options}
                series={chart.series}
                type="bar"
                height={Math.max(140, stats.contracts.length * 64)}
              />

              {/* legend */}
              <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-gray-100 pt-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />
                  Abaixo de 70%
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" />
                  70% – 90%
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500" />
                  Acima de 90%
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-500" />
                  Sem limite
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
